/**
 * Logger utility that supports URL parameter-based logging activation
 */

// Check if logging is enabled via URL parameter or environment variable
export const isLoggingEnabled = (): boolean => {
  // First check URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const enableLogsParam = urlParams.get('enableLogs');
  
  if (enableLogsParam === 'true') {
    return true;
  }
  
  // Fall back to environment variable
  return process.env.REACT_APP_ENABLE_LOGGING === 'true';
};

type LogLevel = 'info' | 'warn' | 'error' | 'debug' | 'success';

const logStyles: Record<LogLevel, string> = {
  info: 'color: #2196F3; font-weight: bold',
  warn: 'color: #FF9800; font-weight: bold',
  error: 'color: #F44336; font-weight: bold',
  debug: 'color: #9C27B0; font-weight: bold',
  success: 'color: #4CAF50; font-weight: bold',
};

const logEmojis: Record<LogLevel, string> = {
  info: '📝',
  warn: '⚠️',
  error: '🚨',
  debug: '🔍',
  success: '✅',
};

const log = (level: LogLevel, message: string, ...args: any[]): void => {
  if (!isLoggingEnabled()) return;

  const emoji = logEmojis[level];
  const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
  
  console.log(
    `%c${emoji} [${level.toUpperCase()}] ${timestamp}:`,
    logStyles[level],
    message,
    ...args
  );
};

export const logger = {
  info: (message: string, ...args: any[]) => log('info', message, ...args),
  warn: (message: string, ...args: any[]) => log('warn', message, ...args),
  error: (message: string, ...args: any[]) => log('error', message, ...args),
  debug: (message: string, ...args: any[]) => log('debug', message, ...args),
  success: (message: string, ...args: any[]) => log('success', message, ...args),
};

// Support both named and default exports
export default logger;